/**
 * Will be removed once all spacetime format calls have been migrated.
 *
 * Temporal formatting utility — a drop-in replacement for spacetime's `.format()`.
 *
 * Supports the same named formats and token strings that spacetime uses,
 * so migrating code is a matter of swapping the call:
 *
 *   // Before (spacetime)
 *   spacetime(date).format('numeric-us')
 *   spacetime(date).format('{year}-{month-iso}-{date-pad}')
 *
 *   // After (temporal)
 *   formatTemporal(toZoned(date), 'numeric-us')
 *   formatTemporal(toZoned(date), '{year}-{month-iso}-{date-pad}')
 *
 *   // Preferred (native)
 *   new Intl.DateTimeFormat('en-US', { dateStyle: 'short' }).format(toDate(zdt))
 */

import 'temporal-polyfill/global';

/* ── Helpers ──────────────────────────────────────────────── */

const pad = (n: number, width: number = 2): string =>
	String(n).padStart(width, '0');

const MONTHS_SHORT = [
	'Jan',
	'Feb',
	'Mar',
	'Apr',
	'May',
	'Jun',
	'Jul',
	'Aug',
	'Sep',
	'Oct',
	'Nov',
	'Dec',
];

const MONTHS_LONG = [
	'January',
	'February',
	'March',
	'April',
	'May',
	'June',
	'July',
	'August',
	'September',
	'October',
	'November',
	'December',
];

export const DAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
export const DAYS_LONG = [
	'Sunday',
	'Monday',
	'Tuesday',
	'Wednesday',
	'Thursday',
	'Friday',
	'Saturday',
];

function ordinal(n: number): string {
	const s = ['th', 'st', 'nd', 'rd'];
	const v = n % 100;
	return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

function hour12(h: number): number {
	return h === 0 ? 12 : h > 12 ? h - 12 : h;
}

function ampm(h: number): string {
	return h < 12 ? 'am' : 'pm';
}

/** Temporal dayOfWeek is 1=Mon…7=Sun; convert to 0=Sun…6=Sat */
function dayOfWeekJS(zdt: Temporal.ZonedDateTime): number {
	return zdt.dayOfWeek === 7 ? 0 : zdt.dayOfWeek;
}

/**
 * Get the timezone abbreviation (e.g. "MST", "EDT") via Intl.
 */
function tzAbbrev(zdt: Temporal.ZonedDateTime): string {
	try {
		const parts = new Intl.DateTimeFormat('en-US', {
			timeZone: zdt.timeZoneId,
			timeZoneName: 'short',
		}).formatToParts(new Date(zdt.epochMilliseconds));
		return (
			parts.find((p) => p.type === 'timeZoneName')?.value ?? zdt.timeZoneId
		);
	} catch {
		return zdt.timeZoneId;
	}
}

/**
 * Format the UTC offset as "+07:00" / "-05:00".
 */
function formatOffset(zdt: Temporal.ZonedDateTime): string {
	const totalMinutes = Math.round(zdt.offsetNanoseconds / 60_000_000_000);
	const sign = totalMinutes >= 0 ? '+' : '-';
	const absMin = Math.abs(totalMinutes);
	return `${sign}${pad(Math.floor(absMin / 60))}:${pad(absMin % 60)}`;
}

/* ── Token map ────────────────────────────────────────────── */

/**
 * Maps spacetime format tokens to functions producing their string value.
 *
 * Reference: https://spacetime.how/#/format
 */
const TOKEN_MAP: Record<string, (zdt: Temporal.ZonedDateTime) => string> = {
	// year
	year: (z) => String(z.year),
	'year-short': (z) => String(z.year).slice(-2),
	'year-iso': (z) => String(z.year),

	// month
	month: (z) => MONTHS_LONG[z.month - 1],
	'month-short': (z) => MONTHS_SHORT[z.month - 1],
	'month-pad': (z) => pad(z.month),
	'month-iso': (z) => pad(z.month),
	'iso-month': (z) => pad(z.month),

	// date (day of month)
	date: (z) => String(z.day),
	'date-pad': (z) => pad(z.day),
	'date-ordinal': (z) => ordinal(z.day),

	// day of week
	day: (z) => DAYS_LONG[dayOfWeekJS(z)],
	'day-short': (z) => DAYS_SHORT[dayOfWeekJS(z)],

	// hour
	hour: (z) => String(hour12(z.hour)),
	'hour-pad': (z) => pad(hour12(z.hour)),
	'hour-24': (z) => String(z.hour),
	'hour-24-pad': (z) => pad(z.hour),

	// minute / second
	minute: (z) => String(z.minute),
	'minute-pad': (z) => pad(z.minute),
	second: (z) => String(z.second),
	'second-pad': (z) => pad(z.second),

	// am/pm
	ampm: (z) => ampm(z.hour),
	AMPM: (z) => ampm(z.hour).toUpperCase(),

	// time shorthand  –  "1:35 pm"
	time: (z) => `${hour12(z.hour)}:${pad(z.minute)} ${ampm(z.hour)}`,
	// 24h time  –  "13:35"
	'time-24': (z) => `${pad(z.hour)}:${pad(z.minute)}`,

	// timezone
	timezone: (z) => tzAbbrev(z),
	offset: (z) => formatOffset(z),

	// numeric-us inline  –  "04/08/2026"
	'numeric-us': (z) => `${pad(z.month)}/${pad(z.day)}/${z.year}`,

	// iso inline
	'iso-short': (z) => `${z.year}-${pad(z.month)}-${pad(z.day)}`,
};

const TOKEN_REGEX = /\{([^}]+)\}/g;

/* ── Named formats ────────────────────────────────────────── */

/**
 * Named format presets matching spacetime's built-in format names.
 * These do NOT use the `{token}` syntax.
 */
const NAMED_FORMAT: Record<string, (zdt: Temporal.ZonedDateTime) => string> = {
	// "2026-04-08"
	'iso-short': (z) => `${z.year}-${pad(z.month)}-${pad(z.day)}`,

	// "2026-04-08T14:35:00.000Z"  (always UTC)
	'iso-utc': (z) => z.toInstant().toString(),

	// "2026-04-08T14:35:00.000-06:00"
	iso: (z) =>
		`${z.year}-${pad(z.month)}-${pad(z.day)}T${pad(z.hour)}:${pad(z.minute)}:${pad(z.second)}.${String(z.millisecond).padStart(3, '0')}${formatOffset(z)}`,

	// "04/08/2026"
	'numeric-us': (z) => `${pad(z.month)}/${pad(z.day)}/${z.year}`,

	// "April 8th, 1:35pm"
	nice: (z) =>
		`${MONTHS_LONG[z.month - 1]} ${ordinal(z.day)}, ${hour12(z.hour)}:${pad(z.minute)}${ampm(z.hour)}`,

	// "Wednesday April 8th, 1:35pm"
	'nice-day': (z) =>
		`${DAYS_LONG[dayOfWeekJS(z)]} ${MONTHS_LONG[z.month - 1]} ${ordinal(z.day)}, ${hour12(z.hour)}:${pad(z.minute)}${ampm(z.hour)}`,

	// "1:35pm"
	time: (z) => `${hour12(z.hour)}:${pad(z.minute)}${ampm(z.hour)}`,

	// "13:35"
	'time-24': (z) => `${pad(z.hour)}:${pad(z.minute)}`,
};

/* ── Public API ───────────────────────────────────────────── */

/**
 * Format a `Temporal.ZonedDateTime` using spacetime-compatible format strings.
 *
 * Supports both named formats (`'iso-short'`, `'numeric-us'`, etc.) and
 * token strings (`'{year}-{month-iso}-{date-pad}'`).
 */
export function formatTemporal(
	zdt: Temporal.ZonedDateTime,
	format: string,
): string {
	// 1. Named format lookup
	const namedFn = NAMED_FORMAT[format];
	if (namedFn) return namedFn(zdt);

	// 2. Token substitution
	return format.replace(TOKEN_REGEX, (_match, token: string) => {
		const fn = TOKEN_MAP[token.toLowerCase()];
		if (fn) return fn(zdt);
		return `{${token}}`; // pass through unrecognized tokens
	});
}
